import type { VehicleInput } from './vehicle';
import type { VehicleState } from './vehicle';

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * POLICE CHASE AI - SIMPLE & RELIABLE
 * 
 * The key insight: Keep it simple. Complex AI breaks.
 * 
 * Rules:
 * 1. Always face the player
 * 2. If not facing player, STOP and TURN
 * 3. When close, orbit around player (movie-style circling)
 * 4. Never drive away from player
 */

type CopAIState = {
  stuckTimer: number;
  lastX: number;
  lastZ: number;
  orbitAngle: number;
  orbitDir: number;
  copId: number;
};

const copStates = new Map<VehicleState, CopAIState>();
let nextCopId = 0;

function getCopState(cop: VehicleState): CopAIState {
  let state = copStates.get(cop);
  if (!state) {
    state = {
      stuckTimer: 0,
      lastX: cop.x,
      lastZ: cop.z,
      orbitAngle: nextCopId * Math.PI,
      orbitDir: nextCopId % 2 === 0 ? 1 : -1,
      copId: nextCopId,
    };
    copStates.set(cop, state);
    nextCopId++;
  }
  return state;
}

export function computeChaseInput(
  cop: VehicleState,
  target: VehicleState,
): VehicleInput {
  const state = getCopState(cop);
  const dt = 0.016;

  const dx = target.x - cop.x;
  const dz = target.z - cop.z;
  const dist = Math.hypot(dx, dz);
  const speed = Math.hypot(cop.vx, cop.vz);
  const targetSpeed = Math.hypot(target.vx, target.vz);

  if (dist < 1) {
    return { throttle: 0, steer: 0, handbrake: false, nitro: false };
  }

  const moved = Math.hypot(cop.x - state.lastX, cop.z - state.lastZ);
  if (moved < 0.05 && speed < 1) {
    state.stuckTimer += dt;
  } else {
    state.stuckTimer = Math.max(0, state.stuckTimer - dt * 2);
  }
  state.lastX = cop.x;
  state.lastZ = cop.z;

  const angleToPlayer = Math.atan2(dx, dz);
  const angleDiff = wrapAngle(angleToPlayer - cop.heading);
  const absAngleDiff = Math.abs(angleDiff);
  const turnDir = angleDiff > 0 ? 1 : -1;

  if (state.stuckTimer > 0.5) {
    state.stuckTimer = 0;
    return { throttle: -0.8, steer: turnDir * 0.8, handbrake: false, nitro: false };
  }

  if (absAngleDiff > Math.PI * 0.7) {
    if (speed < 3) {
      return { throttle: -0.9, steer: turnDir * 0.9, handbrake: false, nitro: false };
    }
    return { throttle: -0.5, steer: turnDir, handbrake: true, nitro: false };
  }

  if (absAngleDiff > Math.PI * 0.4) {
    if (speed > 8) {
      return { throttle: 0, steer: turnDir, handbrake: true, nitro: false };
    }
    return { throttle: 0.4, steer: turnDir, handbrake: false, nitro: false };
  }

  let goalX = target.x;
  let goalZ = target.z;

  if (dist < 13) {
    const fwdX = Math.sin(target.heading);
    const fwdZ = Math.cos(target.heading);
    const rightX = Math.cos(target.heading);
    const rightZ = -Math.sin(target.heading);
    const flank = state.orbitDir * (4.8 + Math.min(4.2, targetSpeed * 0.11));
    goalX = target.x + fwdX * 3.2 + rightX * flank;
    goalZ = target.z + fwdZ * 3.2 + rightZ * flank;
    
    goalX += target.vx * 0.16;
    goalZ += target.vz * 0.16;
  } else if (dist < 40) {
    const predictTime = 0.42 + Math.min(0.35, targetSpeed / 90);
    const rightX = Math.cos(target.heading);
    const rightZ = -Math.sin(target.heading);
    const lanePinch = state.orbitDir * Math.min(8, dist * 0.16);
    goalX = target.x + target.vx * predictTime + rightX * lanePinch;
    goalZ = target.z + target.vz * predictTime + rightZ * lanePinch;
  } else {
    const predictTime = 0.9;
    goalX = target.x + target.vx * predictTime;
    goalZ = target.z + target.vz * predictTime;
  }

  const goalDx = goalX - cop.x;
  const goalDz = goalZ - cop.z;
  const goalAngle = Math.atan2(goalDx, goalDz);
  const goalAngleDiff = wrapAngle(goalAngle - cop.heading);

  const steerGain = speed > 20 ? 2.0 : speed > 10 ? 2.5 : 3.0;
  const steer = Math.max(-1, Math.min(1, goalAngleDiff * steerGain));

  let desiredSpeed: number;
  if (dist > 70) {
    desiredSpeed = Math.max(48, targetSpeed + 18);
  } else if (dist > 50) {
    desiredSpeed = Math.max(42, targetSpeed + 16);
  } else if (dist > 30) {
    desiredSpeed = Math.max(32, targetSpeed + 12);
  } else if (dist > 15) {
    desiredSpeed = Math.max(23, targetSpeed + 8);
  } else {
    desiredSpeed = Math.max(16, targetSpeed + 2);
  }

  const speedErr = desiredSpeed - speed;
  let throttle: number;
  if (speedErr > 5) {
    throttle = 1;
  } else if (speedErr > 0) {
    throttle = 0.6 + speedErr * 0.08;
  } else {
    throttle = Math.max(0.2, 0.5 + speedErr * 0.05);
  }

  if (Math.abs(steer) > 0.6 && speed > 15) {
    throttle *= 0.7;
  }

  const useHandbrake = dist < 18 && Math.abs(goalAngleDiff) > 0.46 && speed > 12;

  const nitro = dist > 60 && speed < 35 && absAngleDiff < 0.3;

  return { throttle, steer, handbrake: useHandbrake, nitro };
}

export function computeEvadeInput(
  driver: VehicleState,
  cop: VehicleState,
): VehicleInput {
  const dx = driver.x - cop.x;
  const dz = driver.z - cop.z;
  const away = Math.atan2(dx, dz);
  let err = away - driver.heading;
  err = Math.atan2(Math.sin(err), Math.cos(err));
  const steer = Math.max(-1, Math.min(1, err * 2.65));
  return { throttle: 1, steer, handbrake: false, nitro: false };
}

export function resetCopAI(): void {
  copStates.clear();
  nextCopId = 0;
}
